import React, { ReactNode } from "react";
import { Flex } from "antd";
import styled from "styled-components";
import { Skeleton, radius } from "@libertify/ui";
import { Statistic } from "./Statistic";
import { Link } from "react-router-dom";
import { useTranslate } from "@refinedev/core";
import { getMetricColor, metricColors, formatPct } from "./utils/";
import { analyticsTheme } from "./analyticsTheme";

const StyledCard = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid ${analyticsTheme.cardBorder};
  border-radius: ${radius.box}px;
  padding: 20px 24px;
  background: ${analyticsTheme.cardBg};
  box-sizing: border-box;
`;

const ClickableCard = styled(Link)`
  display: block;
  width: 100%;
  height: 100%;
  border: 1px solid ${analyticsTheme.cardBorder};
  border-radius: ${radius.box}px;
  padding: 20px 24px;
  background: ${analyticsTheme.cardBg};
  box-sizing: border-box;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    border-color: rgba(20, 184, 166, 0.45);
    box-shadow: 0 0 0 1px rgba(20, 184, 166, 0.45);
    color: inherit;
  }
`;

const HeaderTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const IconWrapper = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $color }) => $color};
  color: #fff;
`;

const MoreLabel = styled.span<{ $color?: string }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color || "inherit"};
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
`;

const SkeletonMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type DetailMetricType = "number" | "percentage";

const formatters: Record<DetailMetricType, (v: number) => string> = {
  number: (v) => v.toLocaleString(),
  percentage: (v) => formatPct(v),
};

type DetailMetric = {
  title: string;
  metric?: number | null;
  type?: DetailMetricType;
  target?: number;
  aim?: "high" | "low";
};

export type DetailStatisticCardProps = {
  icon?: ReactNode;
  iconColor?: string;
  title: string;
  metrics: DetailMetric[];
  to?: string;
  /** Forwarded to the router link when `to` is set. */
  replace?: boolean;
  state?: unknown;
  className?: string;
  loading?: boolean;
};

export const DetailStatisticCard: React.FC<DetailStatisticCardProps> = ({
  icon,
  iconColor,
  title,
  metrics,
  to,
  replace,
  state,
  className,
  loading,
}) => {
  const t = useTranslate();

  if (loading) {
    return (
      <StyledCard className={className}>
        <Flex align="center" gap={10} style={{ marginBottom: 16 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="rectangular" width="50%" height={14} />
        </Flex>
        <MetricsGrid>
          {/* 4 skeleton slots matching the 2x2 metrics grid layout */}
          {[0, 1, 2, 3].map((i) => (
            <SkeletonMetric key={i}>
              <Skeleton variant="text" rows={1} height={12} width="60%" />
              <Skeleton variant="rectangular" height={28} width="70%" />
            </SkeletonMetric>
          ))}
        </MetricsGrid>
      </StyledCard>
    );
  }

  const Wrapper: React.ElementType = to ? ClickableCard : StyledCard;
  const wrapperProps = to ? { to, replace, state, className } : { className };

  return (
    <Wrapper {...(wrapperProps as any)}>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={10}>
          {icon && iconColor ? (
            <IconWrapper $color={iconColor}>{icon}</IconWrapper>
          ) : (
            icon
          )}
          <HeaderTitle>{title}</HeaderTitle>
        </Flex>
        {to && (
          <MoreLabel $color={iconColor}>
            {t("media.projects.analytics.dashboard.summaryCards.more")}
          </MoreLabel>
        )}
      </Flex>

      <MetricsGrid>
        {metrics.map((m) => (
          <div key={m.title}>
            <Statistic
              title={m.title}
              value={
                m.metric == null || Number.isNaN(m.metric)
                  ? "--"
                  : formatters[m.type ?? "number"](m.metric)
              }
              valueStyle={{
                fontSize: 28,
                fontWeight: 600,
                color: getMetricColor(
                  m.metric ?? undefined,
                  m.target,
                  metricColors,
                  m.aim
                ),
              }}
            />
          </div>
        ))}
      </MetricsGrid>
    </Wrapper>
  );
};
